import React, { ReactElement, useEffect } from 'react';
import type { Route } from 'next';
import classNames from 'classnames/bind';

import { useSurveyRequests } from 'api/requests';
import { endpoints } from 'api/endpoints';
import proxyAxios from 'api/proxyAxios';

import { IFormData, IGroup, IQuestion } from 'sections/Feedback/EventsSurvey/types';

import RootLayout from 'layouts/RootLayout';
import Breadcrumbs from 'components/Breadcrumbs';
import { Input } from 'sections/Feedback/EventsSurvey/components/Input';
import { GroupItem } from 'sections/Feedback/EventsSurvey/components/GroupItem';
import { useModal } from 'components/Modal/Modal.hooks';

import styles from 'pages/feedback/events-survey/EventsSurvey.module.scss';

const cn = classNames.bind(styles);

interface IEventsSurvey {
  slug: string;
  surveyGroups: IGroup[] | [];
  surveyQuestions: IQuestion[] | [];
  surveyTitle: string;
  survey_id: number;
};

enum EForm {
  email = 'email',
  company = 'company',
  date = 'date',
};

type RefactoredObject = { question_id: number; items: { option_id: number; result: string }[] };
type UserInputsTypes = { company?: string; email?: string };

export default function EventsSurvey({
  slug,
  surveyGroups,
  surveyQuestions,
  surveyTitle,
  survey_id,
}: IEventsSurvey) {
  const { openModal } = useModal();
  const [ groups, setGroups ] = React.useState<IGroup[] | []>(surveyGroups);
  const [ questions, setQuestions ] = React.useState<IQuestion[] | []>(surveyQuestions);
  const [ title, setTitle ] = React.useState<string>(surveyTitle);
  const [ formData, setFormData ] = React.useState<IFormData[] | []>([]);
  const [ loader, setLoader ] = React.useState<boolean>(false);
  const [ userData, setUserData ] = React.useState<UserInputsTypes>({
    company: '',
    email: '',
  });
  const [ errorsAnswer, setErrorsAnswer ] = React.useState<IFormData[]>([]);
  const [ formErrors, setFormErrors ] = React.useState<UserInputsTypes>({});
  const { sendSurveyFormData } = useSurveyRequests();

  //@ts-ignore
  const onChangeHandler = (event: any, type: string, id: number, question_id: number) => {
    if (event.target.type === 'radio') {
      setFormData((): any => {
        return [
          ...formData.map((item: { id: number; value: string | boolean; question_id: number }) => {
            if (item.id !== id && item.question_id === question_id) {
              return {
                ...item,
                value: false,
              };
            } else if (item.id === id) {
              return {
                ...item,
                value: !item.value,
              };
            } else {
              return item;
            }
          }),
        ];
      });
    } else {
      setFormData((): any => {
        return [
          ...formData.map((item: { id: number; value: string | boolean; question_id: number }) => {
            if (item.id !== id && item.question_id === question_id && item.value === '') {
              return {
                ...item,
                value: false,
              };
            } else if (item.id === id) {
              return {
                ...item,
                value: !item.value,
              };
            } else {
              return item;
            }
          }),
        ];
      });
    }
  };

  const onBlurHandler = (id: number, value: string) => {
    setFormData((): IFormData[] => {
      return [
        ...formData.map((item) => {
          if (item.id === id) {
            return {
              ...item,
              id,
              value,
            };
          } else {
            return item;
          }
        }),
      ];
    });
  };

  const refactorObject = () => {
    const questionItems = [...formData.slice().filter((item) => item.value)].reduce(
      (prevValue: RefactoredObject[], currValue) => {
        const index = prevValue.findIndex((el: RefactoredObject) => el.question_id === currValue.question_id);
        if (index === -1) {
          prevValue.push({
            question_id: currValue.question_id,
            items: [
              {
                option_id: currValue.id,
                result: String(currValue.value),
              },
            ],
          });
        } else {
          prevValue[index] = {
            ...prevValue[index],
            items: [
              ...prevValue[index].items,
              {
                option_id: currValue.id,
                result: String(currValue.value),
              },
            ],
          };
        }

        return prevValue;
      },
      []
    );

    return {
      survey_id: survey_id,
      attr: userData,
      questions: questionItems,
    };
  };

  const scrollToErrorElement = (userInput: [string, string], surveyInput: IFormData) => {
    let anchorElement: HTMLElement | null;

    if (userInput && userInput[0]) {
      anchorElement = document.querySelector(`[name=\"${userInput[0]}\"]`);
    } else {
      const { id } = surveyInput;
      anchorElement = window.document.querySelector(`[name=\"${id}\"]`);
    }

    anchorElement!.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  };

  const sendData = async () => {
    setLoader(true);

    const userCheck = Object.entries(userData).filter((item) => item[1] === '');
    const formDataCheck: IFormData[] = formData
      .filter((item) => item.value === '')
      .sort((item1, item2) => item1.question_id - item2.question_id);

    setFormErrors(userData);
    setErrorsAnswer(formDataCheck);

    const newFormData = refactorObject();

    if (!userCheck.length && !formDataCheck.length) {
      try {
        await sendSurveyFormData(newFormData)
        openModal('info', {
          heading: 'Thank you!',
          paragraphs: [
            <>Your answers has been successfully sent.</>
          ],
          buttonText: 'Main Page',
          buttonUrl: '/',
        });
      } catch (err) {
        throw new Error();
      }
    } else {
      scrollToErrorElement(userCheck[0], formDataCheck[0]);
    }

    setLoader(false);
  };

  const onChangeForm = (name: string, value: string) => {
    setUserData((state) => {
      return {
        ...state,
        [name]: value,
      };
    });
  };

  useEffect(() => {
    if (errorsAnswer.length) {
      setErrorsAnswer(formData);
    }

    if (Object.keys(formErrors).length) {
      setFormErrors(userData);
    }
  }, [formData, userData]);

  useEffect(() => {
    if (questions.length === 0) return;
    setFormData(() => {
      return questions
        .map((item) => {
          const { id: question_id } = item;

          return item.options.map(({ id }) => {
            return {
              question_id,
              id,
              value: '',
            };
          });
          //@ts-ignore
        })
        .flat();
    });
  }, [questions]);

  return (
    <>
      <div className={cn('events-survey')}>
        <div className={cn('container')}>
          <Breadcrumbs mode="dark" mTop={64} links={[{ title: slug.replaceAll('-', ' '), href: '' as Route }]} />

          <h1 className={cn('events-survey__heading')}>
            {title === 'Annual Event Survey'
              ? 'The 1st Digital Freight Alliance Annual Conference Members Experience Survey'
              : title}
          </h1>
          <p className={cn('events-survey__heading-description')}>
            Your answers will help us provide you with the support your business needs to grow.
          </p>

          <div className={cn('events-survey__form-container')}>
            <div className={cn('events-survey__form-item')}>
              <Input
                name={EForm.company}
                type={'static'}
                placeholder={'Company name'}
                onChangeForm={onChangeForm}
                errors={formErrors.company === '' || false}
              />
            </div>
            <div className={cn('events-survey__form-item')}>
              <Input
                name={EForm.email}
                type={'static'}
                placeholder={'Enter your email'}
                onChangeForm={onChangeForm}
                errors={formErrors.email === '' || false}
              />
            </div>
            <div className={cn('events-survey__form-item')}>
              <Input name={EForm.date} type={'static'} />
            </div>
          </div>

          <div className={cn('events-survey__questions-container')}>
            {groups!.map((item) => {
              return (
                <GroupItem
                  onChangeHandler={onChangeHandler}
                  onBlurHandler={onBlurHandler}
                  setFormData={setFormData}
                  dataQuestions={questions}
                  dataGroup={item}
                  key={item.id}
                  formData={formData}
                  errorsAnswer={errorsAnswer}
                />
              );
            })}

            <div className={cn('events-survey__buttons')}>
              <button onClick={sendData} className={cn('events-survey__button-send')}>
                {loader ? <div className={cn('events-survey__preloader')}></div> : ''}
                Send Survey
              </button>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}

export const getServerSideProps = async (context: any) => {
  const slug = context?.params?.slug;

  const isValidSlug = /^[a-z0-9-]+$/.test(slug);

  if (!isValidSlug) {
    return {
      notFound: true,
    };
  }

  const meta = {
    title: slug.replaceAll('-', ' '),
    path: `/feedback/events-survey/${slug}`,
    description: '',
  };
  if (slug === 'survey-to-improve-experience') {
    meta.title = 'Digital Freight Membership Survey | Support Form for Members';
    meta.path = '/feedback/events-survey/survey-to-improve-experience';
    meta.description = 'DF Alliance membership survey form. Fill the form and let us know the support you need from us in order to help you grow your business';
  }

  if (slug === `[slug]`) {
    meta.title = 'DFA Survey - A Survey by DFA Members';
    meta.path = '/feedback/events-survey/[slug]';
    meta.description = 'Our membership survey outlines the main points that your business need to expand and grow. Submit the form now and share your requirements';
  }

  try {
    const { data: questions } = await proxyAxios.get(`${endpoints.survey.configSlug}?slug=${slug}`);

    if (!questions?.id) {
      return {
        notFound: true,
      };
    }

    const { data: groups } = await proxyAxios.get(`${endpoints.survey.tags}?survey_id=${questions.id}`);

    return {
      props: {
        slug: slug,
        surveyGroups: groups,
        surveyQuestions: questions.questions,
        surveyTitle: questions.title,
        survey_id: questions.id,
        ...meta,
      },
    };
  } catch (e) {
    return { props: { slug: slug, surveyGroups: [], surveyQuestions: [], surveyTitle: '', ...meta } };
  }
};

EventsSurvey.getLayout = function getLayout(page: ReactElement) {
  return <RootLayout>{page}</RootLayout>;
};
